import React, { useState, useEffect } from 'react';
import { Button, DatePicker, Space, message, Tag, Spin, Modal, Input, Popconfirm } from 'antd';
import { AlipayCircleOutlined, SyncOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import alipayService from '../../services/alipay';

const { RangePicker } = DatePicker;

const AlipaySync = ({ onSyncSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [manualModalVisible, setManualModalVisible] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [binding, setBinding] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const result = await alipayService.getStatus();
      setStatus(result);
    } catch (error) {
      console.error('检查状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('alipay_bindSuccess') === '1') {
      message.success('支付宝绑定成功！');
      window.history.replaceState({}, '', window.location.pathname);
      checkStatus();
    }
    if (urlParams.has('error')) {
      message.error(urlParams.get('error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleAutoBind = async () => {
    try {
      const authUrl = await alipayService.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      message.error('获取授权链接失败');
    }
  };

  const handleManualBind = async () => {
    if (!authCode.trim()) {
      message.warning('请输入授权码');
      return;
    }
    setBinding(true);
    try {
      await alipayService.bindWithCode(authCode.trim());
      message.success('绑定成功！');
      setManualModalVisible(false);
      setAuthCode('');
      checkStatus();
    } catch (error) {
      message.error(error.response?.data?.message || '绑定失败');
    } finally {
      setBinding(false);
    }
  };

  const handleUnbind = async () => {
    try {
      await alipayService.unbind();
      message.success('解绑成功');
      checkStatus();
    } catch (error) {
      message.error('解绑失败');
    }
  };

  const handleSync = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('请选择日期范围');
      return;
    }
    setSyncing(true);
    try {
      const result = await alipayService.syncBills(
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      message.success(`同步完成！共${result.total}条，导入${result.inserted}条，跳过${result.skipped}条`);
      if (onSyncSuccess) onSyncSuccess();
    } catch (error) {
      if (error.response?.status === 401) {
        message.error('授权已过期，请重新绑定');
        checkStatus();
      } else {
        message.error(error.response?.data?.message || '同步失败');
      }
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !status) {
    return <Spin size="small" />;
  }

  // 未配置
  if (!status?.hasConfig) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
        <AlipayCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />
        <span>支付宝账单同步</span>
        <Tag color="default">未配置</Tag>
        <Button size="small" icon={<LinkOutlined />} onClick={() => (window.location.href = '/settings')}>
          前往配置
        </Button>
      </div>
    );
  }

  // 未绑定
  if (!status?.isBound) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <AlipayCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />
          <span>支付宝账单同步</span>
          <Tag color="warning">未绑定</Tag>
          <Button size="small" type="primary" icon={<LinkOutlined />} onClick={handleAutoBind}>
            绑定支付宝
          </Button>
          <Button size="small" onClick={() => setManualModalVisible(true)}>
            手动输入授权码
          </Button>
        </div>
        <Modal title="手动输入授权码" open={manualModalVisible} onCancel={() => setManualModalVisible(false)} onOk={handleManualBind} confirmLoading={binding} okText="确认绑定">
          <p style={{ color: '#666', marginBottom: 16 }}>如果自动绑定失败，可以手动获取授权码后在此输入。</p>
          <Input placeholder="请输入授权码 (auth_code)" value={authCode} onChange={(e) => setAuthCode(e.target.value)} />
        </Modal>
      </>
    );
  }

  // 已绑定
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 0' }}>
      <AlipayCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />
      <span>支付宝账单同步</span>
      {status.isExpired ? <Tag color="error">已过期</Tag> : <Tag color="success">已绑定</Tag>}

      {status.isExpired ? (
        <Button size="small" type="primary" onClick={handleAutoBind}>重新绑定</Button>
      ) : (
        <>
          <span style={{ marginLeft: 8 }}>同步范围：</span>
          <RangePicker size="small" value={dateRange} onChange={setDateRange} allowClear={false} style={{ width: 240 }} />
          <Button type="primary" size="small" onClick={handleSync} loading={syncing} icon={<SyncOutlined spin={syncing} />}>
            同步账单
          </Button>
        </>
      )}

      <Popconfirm title="确定解绑支付宝？" onConfirm={handleUnbind} okText="确定" cancelText="取消">
        <Button size="small" danger icon={<DisconnectOutlined />}>解绑</Button>
      </Popconfirm>
    </div>
  );
};

export default AlipaySync;
